import { AccountProfileModel } from '@/domain/models/account/account-profile-model'
import { capitalize } from '@mui/material'
import { HelpCenterConfigType } from '../contexts/auth'
import { LinkConstants } from '@/main/routes/helpers/link-constants'
import { loadScript } from '@/main/helpers/load-script-helper'
import { Account } from '@/domain/usecases'
import { AccountWithCustomProperties } from '../contexts/auth'

export const generateNameInitials = (name: string) => {
  const values = name.split(' ')
  const firstName = values.at(0)!.trim()
  let lastName = values.at(-1)!.trim()
  if (firstName === lastName) {
    lastName = lastName.charAt(lastName.length - 1)
  }
  return `${firstName.charAt(0)}${
    lastName ? lastName.charAt(0) : firstName.charAt(1)
  }`.toUpperCase()
}

export const generateUserNameSummarized = (name: string) => {
  const values = name.split(' ')
  const firstName = values.at(0)!.trim()
  let lastName = values.at(-1)!.trim()
  if (firstName === lastName) {
    lastName = ''
  }
  return `${capitalize(firstName.toLocaleLowerCase())} ${capitalize(lastName.toLocaleLowerCase())}`
}

export const generateCapitalizedFirstName = (name: string) => {
  const [firstName] = name.length ? name.trim().split(/\s+/) : ['']
  return capitalize(firstName.toLocaleLowerCase())
}

export const getDisplayName = (accountProfile: AccountProfileModel): string => {
  return accountProfile?.socialName ? accountProfile?.socialName : accountProfile?.name
}

export const getHelpCenterConfig = (
  accountProfile: AccountProfileModel,
  companies: readonly HelpCenterConfigType[]
): HelpCenterConfigType => {
  const currentCompany = accountProfile?.company

  const helpCenterConfig = companies.find(({ companyId: id }) => id === currentCompany?.uniqueId)

  const SKILL_WHATSAPP_PHONE = '5511936197118'
  const currentConfig: HelpCenterConfigType = {
    companyId: helpCenterConfig?.companyId ?? currentCompany?.uniqueId ?? '',
    hasChatBot: helpCenterConfig?.hasChatBot ?? false,
    hasWhatsApp: helpCenterConfig?.hasWhatsApp ?? true,
    hasLink: helpCenterConfig?.hasLink ?? true,
    redirectTo: helpCenterConfig?.redirectTo ?? LinkConstants.SUPPORT.url,
    companyName: helpCenterConfig?.companyName ?? currentCompany?.name ?? '',
    whatsappPhone: helpCenterConfig?.whatsappPhone ?? SKILL_WHATSAPP_PHONE,
    ...(helpCenterConfig?.noPlanConfig && {
      noPlanConfig: {
        message: helpCenterConfig.noPlanConfig.message,
        ctaLink: helpCenterConfig.noPlanConfig.ctaLink,
        ctaLabel: helpCenterConfig.noPlanConfig.ctaLabel
      }
    })
  }

  if (currentConfig.hasChatBot && !currentConfig.hasWhatsApp) {
    loadScript({
      id: 'ze-snippet',
      src: 'https://static.zdassets.com/ekr/snippet.js?key=8b9a1f7a-96f3-4f93-a147-1478fdd13e80'
    }).catch(() => {
      console.log('Error loading chatbot script')
    })
  }

  return currentConfig
}

/**
 * Enriches account profile with feature flag dependent properties
 * This is a centralized function to ensure consistency across the app
 *
 * @param accountProfile - Base account profile data
 * @returns Account with displayName and helpCenterConfig computed from feature flags
 */
export function enrichAccountProfileContext(
  accountProfile: Account.Model,
  companies: readonly HelpCenterConfigType[]
): AccountWithCustomProperties {
  return {
    ...accountProfile,
    displayName: getDisplayName(accountProfile),
    helpCenterConfig: getHelpCenterConfig(accountProfile, companies)
  }
}
